import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ReactComponent as ChevronRight } from '../../shared/icons/chevron-right.svg';
import { A } from '../../routing';
import styles from './desktop-nav.module.scss';

interface NavItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string | React.ReactNode;
  href?: string;
  hrefParams?: Record<string, any>;
  isActive?: boolean;
  hasSubmenu?: boolean;
  submenuOpen?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  external?: boolean;
  className?: string;
}

export const NavItem: React.FC<NavItemProps> = ({
  icon: Icon,
  label,
  href,
  hrefParams = {},
  isActive = false,
  hasSubmenu = false,
  submenuOpen = false,
  onClick,
  disabled = false,
  external = false,
  className = '',
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick(e);
    }
  };

  const itemContent = (
    <>
      {/* Icon container */}
      <div className={styles.iconContainer}>
        <div className={styles.iconBackground} data-active={isActive && !submenuOpen}>
          {hasSubmenu && <ChevronRight className={styles.chevron} data-rotated={submenuOpen} />}
          <Icon className={styles.icon} />
        </div>
      </div>

      {/* Label */}
      <div className={styles.label}>
        {typeof label === 'string' ? (
          <FormattedMessage id={`nav.${label.toLowerCase()}`} defaultMessage={label} />
        ) : (
          label
        )}
      </div>
    </>
  );

  // For external links
  if (external && href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${styles.navItem} ${className}`}
        data-active={isActive}
        data-disabled={disabled}
      >
        {itemContent}
      </a>
    );
  }

  // For internal navigation
  if (href && !disabled && !hasSubmenu) {
    return (
      <A
        href={href}
        hrefParams={hrefParams}
        className={`${styles.navItem} ${className}`}
        data-active={isActive}
        data-disabled={disabled}
      >
        {itemContent}
      </A>
    );
  }

  // For buttons and submenu items
  return (
    <div
      className={`${styles.navItem} ${className}`}
      data-active={isActive || submenuOpen}
      data-disabled={disabled}
      data-has-submenu={hasSubmenu}
      onClick={disabled ? undefined : handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
    >
      {itemContent}
    </div>
  );
};
